import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Shield, FileText, Headphones, BookOpen, PenTool, Users, Phone, NotebookPen, CheckCircle2, Flame, Library, Bookmark, MessageCircle, Sparkles } from 'lucide-react';

const ICON_MAP = {
  shield: Shield,
  'file-text': FileText,
  headphones: Headphones,
  'book-open': BookOpen,
  'pen-tool': PenTool,
  users: Users,
  bookmark: Bookmark,
  'message-circle': MessageCircle,
  sparkles: Sparkles,
};

export default function Resources() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [accountability, setAccountability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [email, setEmail] = useState('');
  const [leadMsg, setLeadMsg] = useState('');

  const load = async () => {
    const [r, a] = await Promise.all([
      api.get('/resources').then(r => r.data).catch(() => []),
      user ? api.get('/accountability/stats').then(r => r.data).catch(() => null) : Promise.resolve(null),
    ]);
    setItems(r);
    setAccountability(a);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const checkin = async () => {
    if (!user) { window.location.href = '/login?next=/resources'; return; }
    setChecking(true);
    try {
      await api.post('/accountability/checkin', { did_journal: true });
      await load();
    } finally { setChecking(false); }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await api.post('/leads', { email, source: 'resources-lead-magnet' });
      setLeadMsg("You're on the list. Check your inbox.");
      setEmail('');
      setTimeout(() => setLeadMsg(''), 4000);
    } catch (e) {
      setLeadMsg('Please enter a valid email.');
    }
  };

  const highlight = items.find(i => i.highlight);
  const leadMagnet = items.find(i => i.is_lead_magnet);
  const others = items.filter(i => !i.highlight && !i.is_lead_magnet);

  return (
    <div className="bg-white text-ink">
      {/* HERO */}
      <section className="relative bg-ink text-white py-14 md:py-20 kc-noise overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(800px 400px at 30% 50%, rgba(212,164,55,0.15), transparent 60%)' }}/>
        <div className="relative kc-container">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] flex items-center gap-2"><Library size={14}/> Resources</div>
          <h1 className="mt-3 font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5.5rem)' }} data-testid="resources-hero-title">Free tools for your<br/><span className="text-[color:var(--kc-gold)]">headspace.</span></h1>
          <p className="mt-4 text-lg text-white/80 max-w-2xl">Downloadable resources, mindset audio, recommended reads, and a private journal accountability check-in. All free. All built to help you take back your mind.</p>
        </div>
      </section>

      <div className="kc-container py-12 md:py-16 space-y-12">
        {/* Crisis Highlight */}
        {highlight && (
          <div className="relative bg-gradient-to-br from-ink to-black text-white rounded-[var(--kc-radius-md)] p-7 md:p-10 border-l-4 border-[color:var(--kc-gold)]" data-testid="crisis-card">
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <div className="w-16 h-16 rounded-full bg-[color:var(--kc-gold)] text-black flex items-center justify-center flex-shrink-0"><Phone size={28}/></div>
              <div className="flex-1">
                <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">{highlight.category}</div>
                <h2 className="font-display uppercase text-2xl md:text-3xl mt-1">{highlight.title}</h2>
                <p className="mt-2 text-white/85">{highlight.description}</p>
              </div>
              <a href={highlight.cta_href} className="h-12 px-6 inline-flex items-center justify-center rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors flex-shrink-0" data-testid="crisis-cta">{highlight.cta_label}</a>
            </div>
          </div>
        )}

        {/* Journal Accountability */}
        <div className="bg-gold-soft border border-[color:var(--kc-gold)] rounded-[var(--kc-radius-md)] p-7 md:p-10" data-testid="accountability-card">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-7">
              <div className="text-[11px] tracking-[0.22em] uppercase text-muted-kc flex items-center gap-2"><NotebookPen size={14}/> Journal Accountability</div>
              <h2 className="font-display uppercase text-3xl md:text-4xl mt-2 leading-tight">Did you journal today?</h2>
              <p className="mt-3 text-ink-2 leading-relaxed max-w-xl">For your privacy, we don't store your journal entries — write them in a notebook, your Notes app, anywhere private. We just track the habit. Streaks build mindset.</p>
            </div>
            <div className="md:col-span-5">
              {!user ? (
                <Link to="/login?next=/resources" className="block w-full h-14 inline-flex items-center justify-center rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-colors" data-testid="accountability-login">Log in to track your streak</Link>
              ) : accountability?.checked_in_today ? (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-[color:var(--kc-gold)] text-sm font-semibold"><CheckCircle2 size={16}/> Logged for today</div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <Stat v={accountability.current_streak} l="Day streak" icon={Flame}/>
                    <Stat v={accountability.total_days_journaled} l="Days journaled" icon={NotebookPen}/>
                  </div>
                </div>
              ) : (
                <div>
                  <button onClick={checkin} disabled={checking} className="w-full h-14 inline-flex items-center justify-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-colors disabled:opacity-50" data-testid="accountability-checkin">
                    <CheckCircle2 size={18}/> {checking ? 'Logging…' : "Yes, I journaled today"}
                  </button>
                  {accountability && (
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <Stat v={accountability.current_streak} l="Day streak" icon={Flame}/>
                      <Stat v={accountability.total_days_journaled} l="Days journaled" icon={NotebookPen}/>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lead Magnet */}
        {leadMagnet && (
          <div className="bg-ink text-white rounded-[var(--kc-radius-md)] p-7 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center" data-testid="lead-magnet-card">
            <div className="md:col-span-7">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] flex items-center gap-2"><FileText size={14}/> {leadMagnet.category}</div>
              <h2 className="font-display uppercase text-3xl md:text-4xl mt-2 leading-tight">{leadMagnet.title}</h2>
              <p className="mt-3 text-white/80">{leadMagnet.description}</p>
            </div>
            <form onSubmit={submitLead} className="md:col-span-5 flex flex-col gap-2">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required className="h-12 px-4 rounded-[var(--kc-radius-sm)] bg-white text-ink focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]" data-testid="lead-email"/>
              <button type="submit" className="h-12 inline-flex items-center justify-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="lead-submit">{leadMagnet.cta_label}</button>
              {leadMsg && <div className="text-sm text-[color:var(--kc-gold)]" data-testid="lead-msg">{leadMsg}</div>}
              <div className="text-[11px] text-white/50">No spam. Unsubscribe anytime.</div>
            </form>
          </div>
        )}

        {/* Other resources */}
        <div>
          <h2 className="font-display uppercase text-2xl md:text-3xl mb-4">More resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {others.map((r) => {
              const Icon = ICON_MAP[r.icon] || FileText;
              const href = r.cta_href || '#';
              const isInternal = typeof href === 'string' && href.startsWith('/');
              const inner = (
                <>
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                  <div className="w-11 h-11 rounded-full bg-ink text-white flex items-center justify-center group-hover:bg-[color:var(--kc-gold)] group-hover:text-black transition-colors"><Icon size={18}/></div>
                  <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-muted-kc">{r.category}</div>
                  <h3 className="mt-1 font-display uppercase text-xl leading-tight">{r.title}</h3>
                  <p className="mt-2 text-ink-2 text-sm leading-relaxed">{r.description}</p>
                  <div className="mt-4 text-sm font-semibold inline-flex items-center gap-1 group-hover:text-gold transition-all group-hover:gap-2">{r.cta_label} →</div>
                </>
              );
              const cls = "group bg-white border border-kc-strong rounded-[var(--kc-radius-md)] p-6 hover:border-gold hover:shadow-kc transition-all relative overflow-hidden hover:-translate-y-0.5";
              if (isInternal) {
                return (<Link key={r.id} to={href} className={cls} data-testid={`resource-${r.id}`}>{inner}</Link>);
              }
              return (<a key={r.id} href={href} className={cls} data-testid={`resource-${r.id}`}>{inner}</a>);
            })}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="text-xs text-muted-kc text-center max-w-2xl mx-auto pt-6 border-t border-kc">
          Resources here are educational and for general guidance only. They are not a substitute for professional medical, psychological, or therapeutic advice. If you are struggling, please reach out to a licensed professional or call/text 988.
        </div>
      </div>
    </div>
  );
}

const Stat = ({ v, l, icon: Icon }) => (
  <div className="bg-white border border-[color:var(--kc-gold)] rounded-[var(--kc-radius-sm)] p-3 flex items-center gap-3">
    <Icon className="text-gold" size={18}/>
    <div>
      <div className="font-display text-2xl text-ink leading-none">{v}</div>
      <div className="text-[10px] tracking-[0.18em] uppercase text-muted-kc mt-0.5">{l}</div>
    </div>
  </div>
);
